import { API_URL } from '../../config/env';
import { Injectable } from '../../decorators/injectable';
import type {
  ISignupLinkPublicService,
  SignupLinkInfo,
  SignupLinkInvalidReason,
} from './signup-link-public.service.interface';

/**
 * Thrown by {@link SignupLinkPublicService.getByToken} when the backend rejects
 * the token. `reason` lets the landing page render a distinct message per
 * failure mode (expired / revoked / full / unknown).
 */
export class SignupLinkError extends Error {
  readonly status: number;
  readonly reason: SignupLinkInvalidReason;

  constructor(reason: SignupLinkInvalidReason, status: number) {
    super(`Signup link request failed: ${status} (${reason})`);
    this.name = 'SignupLinkError';
    this.reason = reason;
    this.status = status;
  }
}

/**
 * Public signup-link lookup service. Uses raw `fetch` (NOT HttpService) because
 * the endpoint is unauthenticated — parity with AdminInvitePublicService.
 */
@Injectable()
export class SignupLinkPublicService implements ISignupLinkPublicService {
  private readonly baseUrl: string;

  constructor() {
    this.baseUrl = API_URL.endsWith('/') ? API_URL : `${API_URL}/`;
  }

  async getByToken(token: string): Promise<SignupLinkInfo> {
    const response = await fetch(`${this.baseUrl}signup-link/${encodeURIComponent(token)}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new SignupLinkError('notFound', 404);
      }
      if (response.status === 410) {
        const body = (await response.json().catch(() => ({}))) as { reason?: string };
        const reason = this.normalizeReason(body.reason);
        throw new SignupLinkError(reason, 410);
      }
      throw new SignupLinkError('notFound', response.status);
    }

    return (await response.json()) as SignupLinkInfo;
  }

  private normalizeReason(reason: string | undefined): SignupLinkInvalidReason {
    if (reason === 'expired' || reason === 'revoked' || reason === 'full') {
      return reason;
    }
    return 'notFound';
  }
}
